import React, { memo } from 'react'
import { Route } from 'react-router-dom'
import { useToastState, ToastState, Toast } from 'transfix-ui/components/Toast'
import { TRACKING_METHOD_TYPE } from 'types/tracking'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { ICarrier, ILocationELD, ITruck, IDriver, IShipmentETA } from 'apps/fleet-planner/types'
import { ShipmentMap } from '../eld-tracking/ShipmentMap'
import { ShipmentMapFooter } from '../eld-tracking/ShipmentMapFooter'
import { TrackingMethodModal } from '../eld-tracking/TrackingMethodModal'
import { getELDState } from '../eld-tracking/utils'
import { useTrackingSummaryQuery } from './graphql/TrackingSummary'

interface ILoadDetailsELDSectionProps {
  shipmentId?: string
  trackingMethod?: TRACKING_METHOD_TYPE
  recentLocations?: ILocationELD[]
  eta?: IShipmentETA
  status: LOAD_STATE
  carrier?: ICarrier
  truck?: Partial<ITruck> | null
  drivers?: IDriver[]
  isCurrentShipment?: boolean
  /**
   * @default false
   */
  isTrackableStatus?: boolean
  isExternal?: boolean
  /**
   * @default '/loads/:id'
   */
  path?: string
}

export const LoadDetailsELDSection = memo<ILoadDetailsELDSectionProps>(
  ({
    trackingMethod,
    recentLocations = [],
    eta,
    isTrackableStatus = false,
    carrier,
    truck,
    status,
    drivers = [],
    shipmentId,
    isCurrentShipment,
    isExternal,
    path = '/loads/:id',
  }) => {
    const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
    const showToastHandler = (newToastState: Omit<ToastState, 'toastOpen'>) => {
      openToast(newToastState)
    }
    const { isKeepTruckinEnabled } = carrier || {}
    const { shipmentDetails } = useMarketplaceContext()
    const { project44Metadata, stops } = shipmentDetails || {}
    const [firstPickup] = stops || []
    const {
      isCarrierProject44Enabled,
      isTruckNumberValid: p44isTruckNumberValid,
      needsValidTruckNumber: p44NeedsValidTruckNumber,
    } = project44Metadata || {}

    const globalDefinitionOfTrackingEnabled =
      useFeatureFlag('useGlobalDefinitionOfTracking') && (isExternal || false)

    const { data } = useTrackingSummaryQuery({
      variables: { shipmentId: `${shipmentId}` },
      skip: !isExternal,
    })
    const { getTrackingSummary } = data || {}
    const { isTracking } = getTrackingSummary || {}

    const { SubtitleComponent, TitleComponent, alertType, pingType } = getELDState({
      recentLocations,
      status,
      trackingMethod,
      isKeepTruckinEnabled,
      isP44Enabled: isCarrierProject44Enabled,
      truck,
      p44NeedsValidTruckNumber,
      p44isTruckNumberValid,
      isCurrentShipment,
      firstPickup,
      shipmentETA: eta,
      isTracking,
      globalDefinitionOfTrackingEnabled,
    })

    return (
      <>
        <ShipmentMap
          recentLocations={recentLocations}
          isTrackableStatus={isTrackableStatus}
          pingType={pingType}
        />
        <ShipmentMapFooter
          trackingMethod={trackingMethod}
          alertType={alertType}
          TitleComponent={TitleComponent}
          SubtitleComponent={SubtitleComponent}
          recentLocations={recentLocations}
          eta={eta}
        />
        <Route exact path={`${path}/tracking-method`}>
          <TrackingMethodModal
            onTrackingMethodUpdate={showToastHandler}
            trackingMethod={trackingMethod}
            shipmentId={shipmentId}
            drivers={drivers}
            carrier={carrier}
            isOpen
          />
        </Route>
        <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      </>
    )
  }
)
